import { useLink } from "@appiodev/xcore-client/xcore-ui";
import { Category } from "@appiodev/xcore-core";
import { useCategoryFilter } from "components/news/useFilters";
import { OptionType } from "design-system/xcore/Select";
import { complement, isNil, prop } from "ramda";
import { useCallback, useMemo } from "react";
import { ValueType } from "react-select";
import { toSet } from "utils/toSet";
import { useLayout } from "xcore";

const useProductFilter = (name: string, categories: Category[], pageCategory?: Category) => {
  const { stringify } = useLayout();
  const validCategories = useMemo(() => categories.filter(c => !!c.content), [categories]);
  const filter = useCategoryFilter(name, validCategories);

  const value = useMemo(
    () => pageCategory && pageCategory.type === name
      ? [{ label: stringify(pageCategory.name), value: pageCategory.id }, ...filter[0].value]
      : filter[0].value,
    [filter, name, pageCategory, stringify]
  );

  const options = useMemo(() => filter[0].options.filter(c => !value.find(v => v.value === c.value)), [filter, value]);

  const onChange = useCallback(
    (q: string, val: ValueType<OptionType>): number[] => {
      const newValues = q === name ? [val].flat() : value as any; // as any because of Ramda typing
      return toSet(newValues.filter(complement(isNil)).map(prop("value")));
    },
    [name, value]
  );

  const getQuery = (v: number[]) => v.map(o => `${name}=${o}`).join("&");

  return {
    value,
    options,
    onChange,
    getQuery
  };
};

export const useProductFilters = () => {
  const { push } = useLink();
  const { header: { nav: { products } }, categories, pageContent: { contentOf } } = useLayout();

  const allCategories = useMemo(() => categories.slice(0, 3).flat(), [categories]);
  const pageCategory = useMemo(() => allCategories.find(c => c.id === contentOf[0]), [allCategories, contentOf]);
  const productType = useProductFilter("product-type", categories[0], pageCategory);
  const productSize = useProductFilter("product-size", categories[1], pageCategory);
  const beamType = useProductFilter("beam-type", categories[2], pageCategory);

  const onChange = (q: string) => (val: ValueType<OptionType>) => {
    const p = productType.onChange(q, val);
    const a = productSize.onChange(q, val);
    const b = beamType.onChange(q, val);

    const sum = p.length + a.length + b.length;

    if (sum === 0) {
      push(products);
    } else if (sum === 1) {
      const category = [...p, ...a, ...b][0];
      const { content } = allCategories.find(c => c.id === category) as Category;
      if (content) {
        push(content);
      }
    } else {
      push(
        products,
        {
          query: "?" + [productType.getQuery(p), productSize.getQuery(a), beamType.getQuery(b)]
            .filter(Boolean)
            .join("&")
        }
      );
    }
  };

  return {
    values: {
      "product-type": productType.value,
      "product-size": productSize.value,
      "beam-type": beamType.value
    },
    options: {
      "product-type": productType.options,
      "product-size": productSize.options,
      "beam-type": beamType.options
    },
    onChange
  };
};
